import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import React, { useMemo, useState, useEffect } from "react";
import Title from "../components/Title";
import { makeRequest } from "../makeRequest";
import frLocale from "@fullcalendar/core/locales/fr";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Modal,
  Snackbar,
  Switch,
} from "@mui/material";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

/* ------------------ HELPERS ------------------ */

const preprocessEvents = (data) =>
  data.map((item) => ({
    _id: item._id,
    members: item.members,
    maxMembers: item.maxMembers,
    start: new Date(item.start),
    startDate: new Date(item.start).toISOString().split("T")[0],
    startTime: new Date(item.start).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    endTime: new Date(item.end).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
  }));

const groupEventsByDate = (data) => {
  const grouped = preprocessEvents(data).reduce((acc, item) => {
    if (!acc[item.startDate]) {
      acc[item.startDate] = { start: item.startDate, events: [] };
    }
    acc[item.startDate].events.push(item);
    return acc;
  }, {});
  return Object.values(grouped);
};

const isRegistrationAllowed = (startTime) => {
  const diff = (new Date(startTime) - new Date()) / (1000 * 60 * 60);
  return diff >= 2;
};

/* ------------------ COMPONENT ------------------ */

function Cours() {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDateKey, setSelectedDateKey] = useState(null);

  const [rememberMe, setRememberMe] = useState(false);
  const [client, setClient] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);

  const [registration, setRegistration] = useState(null);
  const [open, setOpen] = useState(false);

  /* ------------------ DATA ------------------ */

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await makeRequest.get("/cours");
      setEvents(groupEventsByDate(res.data));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = Cookies.get("rememberMe");
    if (saved) {
      setRememberMe(true);
      setClient(JSON.parse(saved));
    }
    fetchData();
  }, []);

  /* ------------------ DERIVED STATE ------------------ */

  const selectedDay = useMemo(() => {
    if (!selectedDateKey) return null;
    return events.find((e) => e.start === selectedDateKey) || null;
  }, [events, selectedDateKey]);

  /* ------------------ CALENDAR ------------------ */

  const handleOpen = (event) => {
    setSelectedDateKey(event.startStr);
    document.getElementById("cours")?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const fullCalendarProps = useMemo(
    () => ({
      plugins: [dayGridPlugin],
      initialView: "dayGridMonth",
      locale: frLocale,
      validRange: { start: new Date() },
      expandRows: true,
      height: "auto",
      events,
      eventClick: (e) => handleOpen(e.event),
    }),
    [events]
  );

  function renderEventContent(eventInfo) {
    const dateKey = eventInfo.event.startStr;
    const day = events.find((e) => e.start === dateKey);

    return (
      <div
        className={`cours ${
          dateKey === selectedDateKey ? "bg-black" : "bg-stone-300"
        } p-2 duration-200 py-8 rounded m-1 text-center cursor-pointer border-4 border-black flex items-center flex-col gap-1 justify-center`}
      >
        {day?.events?.map((item) => (
          <p
            key={item._id}
            className="text-nowrap bg-white border-2 border-black text-black p-1 px-2 rounded-full"
          >
            {item.startTime} - {item.endTime}
          </p>
        ))}
      </div>
    );
  }

  /* ------------------ REGISTER ------------------ */

  const postRegister = async (id) => {
    const res = await makeRequest.post("/registrations", {
      formData: { ...client, id },
    });
    setRegistration(res.data);
  };

  const handleConfirm = async (id) => {
    try {
      const res = await makeRequest.post("/configs/verify/password", {
        value: decodeURIComponent(Cookies.get("coursKey")),
      });

      if (!res.data) {
        Cookies.remove("coursKey");
        alert("Accès refusé. Mot de passe invalide.");
        navigate("/Cours");
        return;
      }

      setShowModal(false);
      await postRegister(id);
      await fetchData();
      setOpen(true);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la vérification du mot de passe");
    }

    rememberMe
      ? Cookies.set("rememberMe", JSON.stringify(client), { expires: 365 })
      : Cookies.remove("rememberMe");
  };

  /* ------------------ RENDER ------------------ */

  return (
    <div className="min-h-screen sm:mt-8 sm:mb-36 md:my-36 flex flex-col items-center">
      {registration && (
        <Snackbar
          open={open}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          autoHideDuration={5000}
        >
          <Alert severity={registration.status} variant="filled">
            {registration.msg}
          </Alert>
        </Snackbar>
      )}

      {/* MODAL */}
      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <label>
              <Switch
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Se souvenir de moi
            </label>

            <h2 className="excelFont text-3xl">Confirmer l'inscription</h2>

            <div className="flex gap-4">
              <Button
                variant="contained"
                color="success"
                onClick={() => handleConfirm(selectedID)}
              >
                Confirmer
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setShowModal(false)}
              >
                Annuler
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* CONTENT */}
      <div className="md:max-w-[80%] sm:max-w-[95%] w-full flex flex-col items-center">
        <div className="my-16">
          <Title word="Nos Cours" />
        </div>

        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <div className="p-4 bg-white w-full excelFont text-2xl text-center my-4 xs-shadow">
              Sélectionnez une date
              <p className="m-2 p-4 bg-black text-white font-semibold poppins text-sm  ">
                Important: L’inscription et l’annulation à un cours doivent être
                effectuées au minimum 2 heures avant l’heure de début prévue.
                Aucune demande ne sera prise en compte après ce délai.
              </p>
            </div>
            <div className="md:max-w-[80%] fade-up sm:max-w-[95%] flex flex-col items-center">
              <div className="w-full h-fit sm:p-4 lg:p-0">
                <FullCalendar
                  eventContent={renderEventContent}
                  {...fullCalendarProps}
                />
              </div>
            </div>

            <div id="cours" className="w-full mt-12">
              {selectedDay?.events && (
                <div className="p-4 fade-up bg-white w-full excelFont text-2xl text-center mb-4 xs-shadow ">
                  Selectionnez un cours
                </div>
              )}
              {selectedDay?.events && (
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 justify-center">
                  {selectedDay.events.map((item, index) => (
                    <div className="flex justify-center">
                    <div
                      key={index}
                      className="w-60 h-60 fade-up excelFont text-3xl border-4 sm-shadow bg-white  border-[#111] rounded-full flex flex-col justify-center items-center"
                    >
                      <p className="excelFont">
                        {item?.startTime} - {item?.endTime}
                      </p>

                      <p className="excelFont text-4xl">
                        [ {item?.members.length} / {item?.maxMembers}]
                      </p>
                      <div className="w-fit h-fit relative mt-4">
                        <img
                          className="absolute w-14 -left-16 z-10 top-1/2 -translate-y-2/3 sm-shadow"
                          src={`${process.env.PUBLIC_URL}/assets/muscle.png`}
                        ></img>
                        <img
                          className="absolute scale-x-[-1] w-14 -right-16 z-10 top-1/2 -translate-y-2/3 sm-shadow"
                          src={`${process.env.PUBLIC_URL}/assets/muscle.png`}
                        ></img>
                        <div
                          className={`${
                            (item?.members.length === item?.maxMembers ||
                              !isRegistrationAllowed(item?.start)) &&
                            "pointer-events-none "
                          } bouncy-transition hover:scale-110`}
                        >
                          <a
                            href={`/Cours/${item?._id}`}
                            onClick={(e) => {
                              rememberMe && e.preventDefault();
                              rememberMe && setShowModal(true);
                              rememberMe && setSelectedID(item?._id);
                            }}
                            className={`${
                              (item?.members.length === item?.maxMembers ||
                                !isRegistrationAllowed(item?.start)) &&
                              "pointer-events-none bg-opacity-30"
                            } bg-black relative text-white title border-4 border-black select-none cursor-pointer fade-in xs-shadow  p-2  rounded-xl text-4xl z-40`}
                            disabled={
                              item?.members.length === item?.maxMembers ||
                              !isRegistrationAllowed(item?.start)
                            }
                          >
                            S'inscrire
                          </a>
                        </div>
                      </div>
                    </div></div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Cours;
